// @ts-nocheck
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

import { EditSquareSmallIcon } from '@pleio/icons/edit-square-small'
import { NewWindowSmallIcon } from '@pleio/icons/new-window-small'
import { OptionsIcon } from '@pleio/icons/options'

import ChatSettings from 'js/chat/components/ChatSettings'
import chatFragment from 'js/chat/fragments/chat'
import Button from 'js/components/Button'
import DropdownButton from 'js/components/DropdownButton'
import FetchMore from 'js/components/FetchMore'
import Flexer from 'js/components/Flexer/Flexer'
import { H4 } from 'js/components/Heading'
import IconButton from 'js/components/IconButton'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'
import Modal from 'js/components/Modal/Modal'
import NavigationMenu from 'js/layout/Navigation/components/NavigationMenu'
import { globalStateContext } from 'js/lib/GlobalState'

import NavigationChatItem from './NavigationChatItem'

const NavigationChatView = ({ user, visible, onHide }) => {
    const { setGlobalState } = useContext(globalStateContext)

    const { t } = useTranslation()

    const [showSettingsModal, setShowSettingsModal] = useState(false)

    const PAGE_SIZE = 10
    const { loading, data, fetchMore, refetch, subscribeToMore } = useQuery(
        GET_CHATS,
        {
            skip: !visible,
            variables: {
                offset: 0,
                limit: PAGE_SIZE,
            },
        },
    )

    useEffect(() => {
        const unsubscribe = subscribeToMore({
            document: ON_CHATS_UPDATE,
            updateQuery: async (prev, { subscriptionData }) => {
                if (!subscriptionData.data) {
                    return prev
                }

                const { action } = subscriptionData.data.onChatsUpdate

                if (action === 'refetch') {
                    refetch()
                }
            },
        })
        return () => unsubscribe()
    }, [subscribeToMore, refetch])

    useEffect(() => {
        // TODO: We manually refetch the query because fetchPolicy: 'cache-and-network' causes the scrollbar to reset on fetchMore
        if (visible === true) {
            refetch()
        }
    }, [visible, refetch])

    const newMessage = () => {
        setGlobalState((newState) => {
            newState.chatNewThread = true
        })
        onHide()
    }

    return (
        <>
            <NavigationMenu>
                <div
                    className="NavigationMenuHeader"
                    style={{
                        padding: '16px 12px 16px 16px',
                    }}
                >
                    <H4
                        style={{
                            marginTop: '4px',
                            marginRight: '4px',
                        }}
                    >
                        {t('chat.latest-chats')}
                    </H4>
                    <Flexer>
                        <Button
                            size="normal"
                            variant="secondary"
                            onClick={newMessage}
                        >
                            <EditSquareSmallIcon
                                style={{ marginRight: '6px' }}
                            />
                            {t('chat.new-message')}
                        </Button>
                        <DropdownButton
                            options={[
                                {
                                    label: t('global.settings'),
                                    onClick: () => {
                                        setShowSettingsModal(true)
                                    },
                                },
                            ]}
                        >
                            <IconButton
                                size="normal"
                                variant="secondary"
                                tooltip={t('global.options')}
                                aria-label={t('action.show-options')}
                            >
                                <OptionsIcon />
                            </IconButton>
                        </DropdownButton>
                    </Flexer>
                </div>
                {!data || loading ? (
                    <LoadingSpinner
                        style={{ marginTop: '-8px', marginBottom: '16px' }}
                    />
                ) : (
                    <FetchMore
                        edges={data.chats.edges}
                        getMoreResults={(data) => data.chats.edges}
                        fetchMore={fetchMore}
                        fetchType="scroll"
                        isScrollableBox={true}
                        fetchCount={10}
                        maxLimit={data.chats.total}
                        className="NavigationMenuList"
                    >
                        <ul>
                            {data.chats.edges.map((chat) => (
                                <NavigationChatItem
                                    key={`navigation-chats-${chat.guid}`}
                                    entity={chat}
                                    onHide={onHide}
                                />
                            ))}
                        </ul>
                    </FetchMore>
                )}

                <a
                    className="NavigationMenuFooter"
                    href="/chat"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t('chat.all-chats')}
                    <NewWindowSmallIcon style={{ marginLeft: '6px' }} />
                </a>
            </NavigationMenu>

            <Modal
                isVisible={showSettingsModal}
                onClose={() => setShowSettingsModal(false)}
                size="small"
                showCloseButton
                title={t('chat.chat-settings')}
            >
                <ChatSettings
                    user={user}
                    onClose={() => setShowSettingsModal(false)}
                />
            </Modal>
        </>
    )
}

const GET_CHATS = gql`
    query NavigationChats($offset: Int, $limit: Int) {
        chats(offset: $offset, limit: $limit) {
            total
            edges {
               ${chatFragment}
            }
        }
    }
`

const ON_CHATS_UPDATE = gql`
    subscription onChatsUpdate {
        onChatsUpdate {
            guid
            action
        }
    }
`

export default NavigationChatView
